import { useState } from "react";
import { Check, Copy, TrendingUp, Tag, X } from "lucide-react";
import { toast } from "sonner";
import { fmtMYTDate } from "@/lib/myt";
import api, { formatApiError } from "@/lib/api";

// Admin-note preset categories (admin notes only — never affect IB/rank/access).
const REMARK_PRESETS = ["IB CHANGED", "MIB - LAN", "MIB - MIRA"];

// Shared per-client action row used by Admin Members.
// email-check / remark reuse the Phase 122 endpoints, always keyed by the member's
// ACTUAL sponsoring IB (ibId prop) — never a selected filter. When ibId is missing the
// IB-scoped controls are disabled; Copy Email and Add Progress stay available.
export function ClientActionBar({ member, ibId, onChanged, onAddProgress }) {
  const [copied, setCopied] = useState(false);
  const [busyCheck, setBusyCheck] = useState(false);
  const [open, setOpen] = useState(false);
  const [presetSel, setPresetSel] = useState(member.remark_preset || "");
  const [note, setNote] = useState(member.remark || "");
  const [savingRemark, setSavingRemark] = useState(false);
  const hasIb = !!ibId;

  const copyEmail = async () => {
    if (!member.email) return;
    try {
      await navigator.clipboard.writeText(member.email);
      setCopied(true);
      toast.success("Email copied");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Copy failed — select the email and copy manually");
    }
  };

  const toggleFound = async () => {
    if (!hasIb) return;
    const next = !member.email_found;
    setBusyCheck(true);
    try {
      const { data } = await api.patch(`/admin/ibs/${ibId}/members/${member.id}/email-check`, { email_found: next });
      onChanged?.({ email_found: data.email_found, email_found_by: data.email_found_by, email_found_at: data.email_found_at });
      toast.success(next ? "Marked as Email Found" : "Unmarked");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusyCheck(false);
    }
  };

  const openPopover = () => {
    // Load current saved values each time the popover opens.
    setPresetSel(member.remark_preset || "");
    setNote(member.remark || "");
    setOpen(true);
  };

  const dirty = presetSel !== (member.remark_preset || "") || note !== (member.remark || "");

  const saveRemark = async () => {
    if (!hasIb) return;
    setSavingRemark(true);
    try {
      // Always send both: the selected preset (or "" to clear) and the separate custom note.
      const { data } = await api.patch(`/admin/ibs/${ibId}/members/${member.id}/remark`, {
        remark: note,
        remark_preset: presetSel,
      });
      onChanged?.({
        remark: data.remark,
        remark_preset: data.remark_preset ?? "",
        remark_updated_by: data.remark_updated_by,
        remark_updated_at: data.remark_updated_at,
      });
      toast.success("Remark saved");
      setOpen(false);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSavingRemark(false);
    }
  };

  return (
    <div className="space-y-1">
      <div className="flex flex-wrap items-center gap-2" data-testid={`client-actions-${member.username}`}>
        <button
          onClick={copyEmail}
          disabled={!member.email}
          data-testid={`client-copy-email-${member.username}`}
          className="shrink-0 text-xs text-[#94A3B8] hover:text-white border border-[#222530] hover:border-[#D4AF37]/40 rounded-lg px-2.5 py-1.5 transition-colors inline-flex items-center gap-1 disabled:opacity-40"
        >
          {copied ? <><Check className="h-3.5 w-3.5 text-emerald-400" /> Copied</> : <><Copy className="h-3.5 w-3.5" /> Copy Email</>}
        </button>

        <label
          className={`flex items-center gap-2 shrink-0 ${hasIb ? "cursor-pointer" : "opacity-40 cursor-not-allowed"}`}
          title={hasIb ? "" : "No sponsoring IB — email check unavailable"}
          data-testid={`client-emailfound-label-${member.username}`}
        >
          <input type="checkbox" checked={!!member.email_found} onChange={toggleFound} disabled={!hasIb || busyCheck} className="sr-only" data-testid={`client-emailfound-${member.username}`} />
          <span className={`h-5 w-5 rounded border flex items-center justify-center transition-colors ${member.email_found ? "bg-[#D4AF37] border-[#D4AF37]" : "border-[#475569] bg-transparent"}`}>
            {member.email_found && <Check className="h-3.5 w-3.5 text-[#0B0C10]" />}
          </span>
          <span className={`text-[10px] font-mono tracking-widest uppercase ${member.email_found ? "text-[#D4AF37]" : "text-[#64748B]"}`}>Email Found</span>
        </label>

        {/* Saved preset chip */}
        {member.remark_preset && (
          <span
            className="shrink-0 inline-flex items-center gap-1 text-[10px] font-mono tracking-widest uppercase rounded-full px-2.5 py-1 border border-[#D4AF37]/40 text-[#D4AF37] bg-[#D4AF37]/10"
            data-testid={`client-remark-chip-${member.username}`}
          >
            <Tag className="h-3 w-3" /> {member.remark_preset}
          </span>
        )}

        {/* Admin remark popover trigger */}
        <div className="relative shrink-0">
          <button
            onClick={openPopover}
            disabled={!hasIb}
            data-testid={`client-remark-open-${member.username}`}
            className="text-xs text-[#94A3B8] hover:text-white border border-[#222530] hover:border-[#D4AF37]/40 rounded-lg px-2.5 py-1.5 transition-colors inline-flex items-center gap-1 disabled:opacity-40"
            title={hasIb ? "" : "No sponsoring IB — remark N/A"}
          >
            Admin remark…
          </button>

          {open && (
            <>
              <div className="fixed inset-0 z-20" onClick={() => setOpen(false)} data-testid={`client-remark-backdrop-${member.username}`} />
              <div
                className="absolute left-0 z-30 mt-2 w-72 card-lux p-4 rounded-xl border border-[#D4AF37]/25 shadow-[0_18px_40px_rgba(0,0,0,0.5)]"
                data-testid={`client-remark-popover-${member.username}`}
              >
                <div className="flex items-center justify-between mb-3">
                  <p className="text-[10px] font-mono tracking-widest uppercase text-[#D4AF37]">Admin Remark</p>
                  <button onClick={() => setOpen(false)} className="text-[#64748B] hover:text-white" data-testid={`client-remark-x-${member.username}`}><X className="h-4 w-4" /></button>
                </div>

                <div className="flex flex-col gap-2 mb-3">
                  {REMARK_PRESETS.map((p) => {
                    const active = presetSel === p;
                    return (
                      <button
                        key={p}
                        onClick={() => setPresetSel(active ? "" : p)}
                        data-testid={`client-remark-preset-${member.username}-${p.replace(/\s|-/g, "").toLowerCase()}`}
                        className={`w-full text-left text-xs font-mono tracking-wider uppercase rounded-lg px-3 py-2 border transition-colors ${active ? "bg-[#D4AF37] border-[#D4AF37] text-[#0B0C10]" : "border-[#222530] text-[#94A3B8] hover:border-[#D4AF37]/40"}`}
                      >
                        {p}{active && <Check className="h-3.5 w-3.5 inline ml-2" />}
                      </button>
                    );
                  })}
                </div>

                <p className="text-[9px] font-mono tracking-widest uppercase text-[#64748B] mb-1">Additional remark</p>
                <textarea
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  maxLength={1000}
                  rows={2}
                  placeholder="Custom note (optional)…"
                  data-testid={`client-remark-note-${member.username}`}
                  className="w-full bg-[#0B0C10] border border-[#222530] focus:border-[#D4AF37]/50 rounded-lg px-3 py-2 text-xs text-white outline-none resize-none"
                />

                <div className="flex justify-end gap-2 mt-3">
                  <button
                    onClick={() => setOpen(false)}
                    data-testid={`client-remark-cancel-${member.username}`}
                    className="text-[10px] font-mono tracking-widest uppercase rounded-lg px-3 py-1.5 border border-[#222530] text-[#94A3B8] hover:border-[#D4AF37]/40 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={saveRemark}
                    disabled={savingRemark || !dirty}
                    data-testid={`client-remark-save-${member.username}`}
                    className="text-[10px] font-mono tracking-widest uppercase rounded-lg px-3 py-1.5 border border-[#D4AF37]/40 text-[#D4AF37] hover:bg-[#D4AF37]/10 transition-colors disabled:opacity-40"
                  >
                    {savingRemark ? "Saving…" : "Save"}
                  </button>
                </div>
              </div>
            </>
          )}
        </div>

        <button
          onClick={onAddProgress}
          data-testid={`client-add-progress-${member.username}`}
          className="shrink-0 text-xs text-[#D4AF37] border border-[#D4AF37]/40 hover:bg-[#D4AF37]/10 rounded-lg px-2.5 py-1.5 transition-colors inline-flex items-center gap-1"
        >
          <TrendingUp className="h-3.5 w-3.5" /> Add Progress
        </button>
      </div>

      {member.remark && (
        <p className="text-[10px] text-[#94A3B8] font-mono pl-0.5 break-words" data-testid={`client-remark-text-${member.username}`}>
          “{member.remark}”
        </p>
      )}
      {member.remark_updated_by && (member.remark || member.remark_preset) && (
        <p className="text-[10px] text-[#64748B] font-mono pl-0.5" data-testid={`client-remark-meta-${member.username}`}>
          noted by {member.remark_updated_by}{member.remark_updated_at ? ` · ${fmtMYTDate(member.remark_updated_at)}` : ""}
        </p>
      )}
    </div>
  );
}
